import {API} from 'aws-amplify';
import {createSlice} from '@reduxjs/toolkit';
import queryStringHelper from 'query-string';

import {ENDPOINT} from '../../constants';

function startLoading(state) {
  state.isLoading = true;
}

function loadingFailed(state, action) {
  state.isLoading = false;
  state.error = action.payload;
}

export const builderSlice = createSlice({
  name: 'builder',
  initialState: {
    isLoading: false,
    lessons: {},
    exercises: {},
    students: {},
  },
  reducers: {
    getStudentsStart: startLoading,
    getStudentsSuccess: (state, action) => {
      state.students = action.payload.reduce(
        (acc, student) => ({
          ...acc,
          [student.id]: student,
        }),
        {},
      );
      state.isLoading = false;
      state.error = null;
    },
    getStudentsFailure: loadingFailed,
    getStudentStart: startLoading,
    getStudentSuccess: (state, action) => {
      const studentID = action.payload.id;
      state.students[studentID] = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    getStudentFailure: loadingFailed,
    createStudentStart: startLoading,
    createStudentSuccess: (state, action) => {
      const studentID = action.payload.id;
      state.students[studentID] = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    createStudentFailure: loadingFailed,
    updateStudentStart: startLoading,
    updateStudentSuccess: (state, action) => {
      const studentID = action.payload._id;
      state.students[studentID] = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    updateStudentFailure: loadingFailed,
    getLessonsStart: startLoading,
    getLessonsSuccess: (state, action) => {
      state.lessons = action.payload.reduce(
        (acc, lesson) => ({
          ...acc,
          [lesson._id]: lesson,
        }),
        {},
      );
      state.isLoading = false;
      state.error = null;
    },
    getLessonsFailure: loadingFailed,
    getLessonStart: startLoading,
    getLessonSuccess: (state, action) => {
      const lessonID = action.payload._id;
      state.lessons[lessonID] = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    getLessonFailure: loadingFailed,
    createLessonStart: startLoading,
    createLessonSuccess: (state, action) => {
      const lessonID = action.payload._id;
      state.lessons[lessonID] = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    createLessonFailure: loadingFailed,
    createExerciseStart: startLoading,
    createExerciseSuccess: (state, action) => {
      const exerciseID = action.payload._id;
      state.exercises[exerciseID] = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    createExerciseFailure: loadingFailed,
    updateLessonStart: startLoading,
    updateLessonSuccess: (state, action) => {
      const lessonID = action.payload._id;
      state.lessons[lessonID] = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    updateLessonFailure: loadingFailed,
    deleteLessonStart: startLoading,
    deleteLessonSuccess: (state, action) => {
      const lessonID = action.payload;
      delete state.lessons[lessonID];
      state.isLoading = false;
      state.error = null;
    },
    deleteLessonFailure: loadingFailed,
    deleteExerciseStart: startLoading,
    deleteExerciseSuccess: (state, action) => {
      const exerciseID = action.payload;
      delete state.exercises[exerciseID];
      state.isLoading = false;
      state.error = null;
    },
    deleteExerciseFailure: loadingFailed,
    updateExerciseStart: startLoading,
    updateExerciseSuccess: (state, action) => {
      const exerciseID = action.payload._id;
      state.exercises[exerciseID] = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    updateExerciseFailure: loadingFailed,
    getExerciseStart: startLoading,
    getExerciseSuccess: (state, action) => {
      const exerciseID = action.payload._id;
      state.exercises[exerciseID] = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    getExerciseFailure: loadingFailed,
    getExercisesStart: startLoading,
    getExercisesSuccess: (state, action) => {
      action.payload.forEach((exercise) => {
        state.exercises[exercise._id] = exercise;
      });
      state.isLoading = false;
      state.error = null;
    },
    getExercisesFailure: loadingFailed,
  },
});

export const {
  getStudentsStart,
  getStudentsSuccess,
  getStudentsFailure,
  getStudentStart,
  getStudentSuccess,
  getStudentFailure,
  createStudentStart,
  createStudentSuccess,
  createStudentFailure,
  updateStudentStart,
  updateStudentSuccess,
  updateStudentFailure,
  getLessonsStart,
  getLessonsSuccess,
  getLessonsFailure,
  getLessonStart,
  getLessonSuccess,
  getLessonFailure,
  createLessonStart,
  createLessonSuccess,
  createLessonFailure,
  updateLessonStart,
  updateLessonSuccess,
  updateLessonFailure,
  deleteLessonStart,
  deleteLessonSuccess,
  deleteLessonFailure,
  createExerciseStart,
  createExerciseSuccess,
  createExerciseFailure,
  updateExerciseStart,
  updateExerciseSuccess,
  updateExerciseFailure,
  getExerciseStart,
  getExerciseSuccess,
  getExerciseFailure,
  getExercisesStart,
  getExercisesSuccess,
  getExercisesFailure,
  deleteExerciseStart,
  deleteExerciseSuccess,
  deleteExerciseFailure,
} = builderSlice.actions;

export const fetchStudents = () => async (dispatch) => {
  try {
    dispatch(getStudentsStart());

    const students = await API.get(ENDPOINT, '/students');
    dispatch(getStudentsSuccess(students));
  } catch (error) {
    dispatch(getStudentsFailure(error.toString()));
  }
};

export const fetchStudent = (studentID) => async (dispatch) => {
  try {
    dispatch(getStudentStart());

    const student = await API.get(ENDPOINT, `/students/${studentID}`);
    dispatch(getStudentSuccess(student));
  } catch (error) {
    dispatch(getStudentFailure(error.toString()));
  }
};

export const createStudent =
  (firstName, lastName, school, gradeLevel, birthDate, gender, races = [], ethnicity, language, tags = []) =>
  async (dispatch) => {
    try {
      dispatch(createStudentStart());

      const request = {
        body: {
          firstName,
          lastName,
          school,
          gradeLevel,
          birthDate,
          gender,
          races,
          ethnicity,
          language,
          tags,
        },
      };
      const student = await API.post(ENDPOINT, '/students', request);
      dispatch(createStudentSuccess(student));
      return student;
    } catch (error) {
      dispatch(createStudentFailure(error.toString()));
    }
  };

export const updateStudent = (studentID, partialStudent) => async (dispatch) => {
  try {
    dispatch(updateStudentStart());

    const request = {
      body: partialStudent,
    };
    const student = await API.patch(ENDPOINT, `/students/${studentID}`, request);
    dispatch(updateStudentSuccess(student));
    return student;
  } catch (error) {
    dispatch(updateStudentFailure(error.toString()));
  }
};

// Optional: userGroupID, userID, visibility
export const fetchLessons =
  ({userGroupID, userID, visibility}) =>
  async (dispatch) => {
    try {
      dispatch(getLessonsStart());

      let queryString = queryStringHelper.stringify({userGroupID, userID, visibility});
      if (queryString) queryString = `?${queryString}`;

      const lessons = await API.get(ENDPOINT, `/lessons${queryString}`);
      dispatch(getLessonsSuccess(lessons));
    } catch (error) {
      dispatch(getLessonsFailure(error.toString()));
    }
  };

export const fetchLesson = (lessonID) => async (dispatch) => {
  try {
    dispatch(getLessonStart());
    const lesson = await API.get(ENDPOINT, `/lessons/${lessonID}`);

    // lesson exercises
    const exerciseIDs = Array.from(new Set(lesson.steps.reduce((acc, value) => acc.concat(value.exercises), [])));
    const promises = exerciseIDs.map((exerciseID) => API.get(ENDPOINT, `/exercises/${exerciseID}`));
    const exercises = await Promise.all(promises);

    dispatch(getExercisesSuccess(exercises));
    dispatch(getLessonSuccess(lesson));
    return lesson;
  } catch (error) {
    dispatch(getLessonFailure(error.toString()));
  }
};

export const createLesson = (name, visibility) => async (dispatch) => {
  try {
    dispatch(createLessonStart());

    const request = {
      body: {
        name,
        visibility,
      },
    };
    const lesson = await API.post(ENDPOINT, '/lessons', request);
    dispatch(createLessonSuccess(lesson));
    return lesson;
  } catch (error) {
    dispatch(createLessonFailure(error.toString()));
  }
};

export const updateLesson = (lessonID, partialLesson) => async (dispatch) => {
  try {
    dispatch(updateLessonStart());

    const request = {
      body: partialLesson,
    };
    const lesson = await API.patch(ENDPOINT, `/lessons/${lessonID}`, request);
    dispatch(updateLessonSuccess(lesson));
  } catch (error) {
    dispatch(updateLessonFailure(error.toString()));
  }
};

export const deleteLesson = (lessonID) => async (dispatch) => {
  try {
    dispatch(deleteLessonStart());

    await API.del(ENDPOINT, `/lessons/${lessonID}`);
    dispatch(deleteLessonSuccess(lessonID));
  } catch (error) {
    dispatch(deleteLessonFailure(error.toString()));
  }
};

export const createExercise =
  (name, type, description, visibility, config, tags = [], instructions) =>
  async (dispatch) => {
    try {
      dispatch(createExerciseStart());

      const request = {
        body: {
          name,
          type,
          description,
          visibility,
          tags,
          ...(instructions && {instructions}),
          config,
        },
      };
      const exercise = await API.post(ENDPOINT, '/exercises', request);
      dispatch(createExerciseSuccess(exercise));
      return exercise;
    } catch (error) {
      dispatch(createExerciseFailure(error.toString()));
    }
  };

export const updateExercise = (exerciseID, partialExercise) => async (dispatch) => {
  try {
    dispatch(updateExerciseStart());

    const request = {
      body: partialExercise,
    };
    const exercise = await API.patch(ENDPOINT, `/exercises/${exerciseID}`, request);
    dispatch(updateExerciseSuccess(exercise));
  } catch (error) {
    dispatch(updateExerciseFailure(error.toString()));
  }
};

export const deleteExercise = (exerciseID) => async (dispatch) => {
  try {
    dispatch(deleteExerciseStart());

    await API.del(ENDPOINT, `/exercises/${exerciseID}`);
    dispatch(deleteExerciseSuccess(exerciseID));
  } catch (error) {
    dispatch(deleteExerciseFailure(error.toString()));
    throw error;
  }
};

export const fetchExercise = (exerciseID) => async (dispatch) => {
  try {
    dispatch(getExerciseStart());
    const exercise = await API.get(ENDPOINT, `/exercises/${exerciseID}`);

    dispatch(getExerciseSuccess(exercise));
    return exercise;
  } catch (error) {
    dispatch(getExerciseFailure(error.toString()));
  }
};

// Optional: userGroupID, userID, visibility, type
export const fetchExercises =
  ({userGroupID, userID, visibility, type}) =>
  async (dispatch) => {
    try {
      dispatch(getExercisesStart());

      let queryString = queryStringHelper.stringify({userGroupID, userID, visibility, type});
      if (queryString) queryString = `?${queryString}`;

      const exercises = await API.get(ENDPOINT, `/exercises${queryString}`);
      dispatch(getExercisesSuccess(exercises));
      return exercises;
    } catch (error) {
      dispatch(getExercisesFailure(error.toString()));
    }
  };

export const selectStudents = (state) => Object.values(state.builder.students);
export const selectLessons = (state) => Object.values(state.builder.lessons);
export const selectExercises = (state) => Object.values(state.builder.exercises);
export const selectIsLoading = (state) => state.builder.isLoading;

export default builderSlice.reducer;
